/**
 * @file src/local/analysis/localAnalysisQueue.js
 * @description One queue, one worker, one file at a time.
 *
 * The header's "analysing 1 of 3" and the card's percentage both read from here. Files are
 * analysed strictly in order — the point of the queue is that a folder dropped mid-set never
 * competes with itself for the CPU the music needs — and the main-thread part (opening the file,
 * decoding) runs just ahead of the worker so the decode of the next file overlaps the analysis of
 * the current one at most by one.
 *
 * Nothing here touches the transport: the music plays through an import. What this DOES cost the
 * main thread is the changeover between two files — opening one, and `decodeAudioData` turning it
 * into raw samples, which for a long stereo track is hundreds of megabytes allocated and released
 * in one go. That is felt as a brief interruption on some files, and it is the price of analysing
 * on import. Moving the decode into the worker (WebCodecs) is what would remove it.
 */
import { LOCAL_PEAKS_BITS, LOCAL_PEAKS_PIXELS_PER_SECOND } from '../localAudioConfig.js';
import { readAudioTags } from '../tags/readAudioTags.js';

const DECODE_SAMPLE_RATE = 48000;

/** @typedef {{ id: string, phase: 'queued'|'decoding'|'peaks'|'tempo'|'loudness'|'done'|'failed'|'cancelled', fraction: number }} JobState */

let worker = null;
const listeners = new Set();
const jobs = new Map(); // id → { id, getBytes, onCoarse, onDone, onError, state }
const order = []; // ids waiting
let active = null; // id
let runCount = { done: 0, total: 0 };
/** The last whole-queue picture, kept until something in the queue moves. See `readQueueState`. */
let snapshot = null;

/**
 * The queue moved. Drop the cached picture and tell whoever is listening.
 *
 * Deliberately does NOT build the picture: a folder of five hundred files emits five hundred times
 * as it is enqueued, and building the state of every job on each of those is the same square cost
 * `readJobState` exists to avoid, just moved earlier. Listeners ask for what they need — `readJobState`
 * for one file, `isAnalysisRunning` for the yes/no, `readQueueState` for the whole thing.
 */
function emit() {
  snapshot = null;
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.warn('[local] analysis listener failed', error);
    }
  });
}

function getWorker() {
  if (worker) return worker;
  return attachWorker(new Worker(new URL('./localAnalysis.worker.js', import.meta.url), { type: 'module' }));
}

function attachWorker(instance) {
  worker = instance;
  worker.onmessage = (event) => {
    const message = event.data || {};
    const job = jobs.get(message.id);
    if (!job) return;
    if (message.type === 'progress') {
      job.state = { id: job.id, phase: message.phase, fraction: message.fraction };
      emit();
      return;
    }
    if (message.type === 'coarse') {
      job.state = { id: job.id, phase: 'tempo', fraction: 0 };
      Promise.resolve(job.onCoarse?.({ peaks: message.peaks, durationSec: message.durationSec })).catch((error) =>
        console.warn('[local] coarse result handler failed', error),
      );
      emit();
      return;
    }
    if (message.type === 'tempo') {
      job.state = { id: job.id, phase: 'loudness', fraction: 0 };
      // A null tempo is a result, not a miss: it is stored so the entry stops looking un-analysed.
      Promise.resolve(job.onTempo?.({ tempoBpm: message.tempoBpm })).catch((error) =>
        console.warn('[local] tempo result handler failed', error),
      );
      emit();
      return;
    }
    if (message.type === 'done') {
      job.state = { id: job.id, phase: 'done', fraction: 1 };
      void finishJob(job, () => job.onDone?.({ loudness: message.loudness, durationSec: message.durationSec }));
      return;
    }
    if (message.type === 'cancelled') {
      job.state = { id: job.id, phase: 'cancelled', fraction: 0 };
      void finishJob(job, null);
      return;
    }
    if (message.type === 'error') {
      job.state = { id: job.id, phase: 'failed', fraction: 0 };
      void finishJob(job, () => job.onError?.(new Error(message.message)));
    }
  };
  worker.onerror = (event) => {
    console.warn('[local] analysis worker error', event?.message || event);
    // A crashed worker is not reused: the next job gets a fresh one.
    try {
      worker?.terminate();
    } catch {
      /* already gone */
    }
    worker = null;
    if (active) {
      const job = jobs.get(active);
      if (job) {
        job.state = { id: job.id, phase: 'failed', fraction: 0 };
        void finishJob(job, () => job.onError?.(new Error(event?.message || 'analysis worker failed')));
      }
    }
  };
  return worker;
}

async function finishJob(job, callback) {
  // The caller set this job's final state before calling in, and the commit below is awaited —
  // so the picture is already out of date here, before anything emits.
  snapshot = null;
  // The result is COMMITTED before the queue says the job is gone: a "held playback" batch resumes
  // on the idle that follows, and it must not resume before the last loudness is stored.
  try {
    await callback?.();
  } catch (error) {
    console.warn('[local] analysis result handler failed', error);
  }
  jobs.delete(job.id);
  if (active === job.id) active = null;
  if (job.state.phase === 'done') runCount.done += 1;
  else runCount.total = Math.max(0, runCount.total - 1);
  emit();
  void pump();
}

async function decode(buffer) {
  if (typeof OfflineAudioContext === 'undefined') {
    throw new Error('This browser cannot decode audio here.');
  }
  const context = new OfflineAudioContext(1, 1, DECODE_SAMPLE_RATE);
  const decoded = await context.decodeAudioData(buffer);
  const channels = [];
  for (let c = 0; c < decoded.numberOfChannels; c += 1) channels.push(decoded.getChannelData(c));
  return { channels, sampleRate: decoded.sampleRate };
}

async function pump() {
  if (active || order.length === 0) return;
  const id = order.shift();
  const job = jobs.get(id);
  if (!job) {
    // The queue moved even though nothing started: a cached picture would still list this id.
    snapshot = null;
    void pump();
    return;
  }
  active = id;
  job.state = { id, phase: 'decoding', fraction: 0 };
  emit();
  let decoded;
  try {
    const bytes = await job.getBytes();
    if (job.cancelRequested) {
      job.state = { id, phase: 'cancelled', fraction: 0 };
      void finishJob(job, null);
      return;
    }
    const buffer = bytes instanceof ArrayBuffer ? bytes : await bytes.arrayBuffer();
    // What the file says about itself, read here because this is the one moment its bytes are
    // open — and BEFORE the decode, which detaches the buffer it would have been read from.
    // Walking a header is microseconds beside decoding the audio, and a file with nothing to say
    // costs a magic-number check; neither is worth a second pass over the disk.
    await reportTags(job, buffer);
    decoded = await decode(buffer);
  } catch (error) {
    job.state = { id, phase: 'failed', fraction: 0 };
    void finishJob(job, () => job.onError?.(error));
    return;
  }
  if (job.cancelRequested) {
    job.state = { id, phase: 'cancelled', fraction: 0 };
    void finishJob(job, null);
    return;
  }
  job.state = { id, phase: 'peaks', fraction: 0 };
  emit();
  // Creating the worker or handing it the buffers can itself fail (a CSP that refuses module
  // workers, a detached buffer); that is this job's failure, not a queue that stops answering.
  try {
    const transfer = decoded.channels.map((channel) => channel.buffer);
    getWorker().postMessage(
      {
        type: 'analyse',
        id,
        channels: decoded.channels,
        sampleRate: decoded.sampleRate,
        pixelsPerSecond: LOCAL_PEAKS_PIXELS_PER_SECOND,
        bits: LOCAL_PEAKS_BITS,
      },
      transfer,
    );
  } catch (error) {
    job.state = { id, phase: 'failed', fraction: 0 };
    void finishJob(job, () => job.onError?.(error));
  }
}

/**
 * Hand the job whatever the file's tags say, if it asked and if there is anything.
 *
 * A tag that cannot be read is not a failure of the analysis: the file still decodes, still plays,
 * and still has the name it had. So this never lets a parse take the job down with it.
 */
async function reportTags(job, buffer) {
  if (typeof job.onTags !== 'function') return;
  try {
    // The picture comes back as a VIEW into `buffer`, which the decode below is about to detach.
    // The handler is awaited here for exactly that reason: whatever it wants to keep, it copies
    // before this returns.
    const tags = await readAudioTags(buffer, { picture: true });
    if (tags.title || tags.artist || tags.album || tags.picture) await job.onTags(tags);
  } catch (error) {
    console.warn('[local] the tags of this file could not be read', error);
  }
}

/**
 * Queue one entry for analysis.
 * @param {{ id: string, getBytes: () => Promise<Blob|ArrayBuffer>, onTags?: Function, onCoarse?: Function, onTempo?: Function, onDone?: Function, onError?: Function }} job
 */
export function enqueueAnalysis(job) {
  if (!job?.id || jobs.has(job.id)) return;
  jobs.set(job.id, { ...job, state: { id: job.id, phase: 'queued', fraction: 0 } });
  order.push(job.id);
  if (runCount.total === 0) runCount.done = 0;
  runCount.total += 1;
  emit();
  void pump();
}

/** Drop a job. A job in the worker is asked to stop at its next slice. */
export function cancelAnalysis(id) {
  const job = jobs.get(id);
  if (!job) return;
  const at = order.indexOf(id);
  if (at >= 0) {
    order.splice(at, 1);
    jobs.delete(id);
    runCount.total = Math.max(0, runCount.total - 1);
    emit();
    return;
  }
  if (active !== id) return;
  // Still opening or decoding on this thread: the pump checks the flag at its next step. Already in
  // the worker: ask it to stop at its next slice.
  job.cancelRequested = true;
  if (worker) worker.postMessage({ type: 'cancel', id });
}

/** Whether anything is being analysed or waiting to be. O(1); no picture is built for it. */
export function isAnalysisRunning() {
  return Boolean(active) || order.length > 0;
}

/**
 * One job's state, or null — the question almost every caller actually has.
 *
 * A card asks "is THIS file being analysed?", and it asks once per card on every redraw. Answering
 * it out of `readQueueState()` meant copying the state of every job in the queue to read one of
 * them, so a rail of five hundred cards did a quarter of a million copies per frame and the import
 * of a big folder became a wait. This is the O(1) answer; `readQueueState` is for the whole picture.
 */
export function readJobState(id) {
  return jobs.get(id)?.state ?? null;
}

/**
 * The whole queue, for the surfaces that want all of it at once.
 *
 * The returned object is SHARED and cached until the queue next moves — read it, never write to it.
 * For one file ask `readJobState`; for the yes/no ask `isAnalysisRunning`.
 *
 * @returns {{ active: JobState|null, queued: string[], done: number, total: number, running: boolean, jobs: Record<string, JobState> }}
 */
export function readQueueState() {
  const running = Boolean(active) || order.length > 0;
  if (!running && runCount.total > 0 && runCount.done >= runCount.total) {
    runCount = { done: 0, total: 0 };
  }
  // Built once per change, not once per caller: the surfaces that want the whole picture read it
  // several times in a single redraw, and the queue only moves when `emit` says it did.
  if (!snapshot) {
    const states = {};
    jobs.forEach((job, id) => {
      states[id] = job.state;
    });
    snapshot = {
      active: active ? (jobs.get(active)?.state ?? null) : null,
      queued: order.slice(),
      done: runCount.done,
      total: runCount.total,
      running,
      jobs: states,
    };
  }
  return snapshot;
}

export function subscribeToAnalysis(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

